import { PropsWithChildren, StrictMode, useEffect, useState } from "react";
import { CookiesProvider } from "react-cookie";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";
import ProvidersRoot from "@app/ProvidersRoot";
import CASBlock from "@components/CASBlock";
import PackageVersionTag from "@components/PackageVersionTag/PackageVersionTag";
import PageGeneratedAtTag from "@components/PageGeneratedAtTag/PageGeneratedAtTag";
import AdvertisementOverlay from "@features/advertisement/components/AdvertisementOverlay";
import useAllCustomHooks from "@hooks/useAllCustomHooks";
import usePodcastPlayerStyle from "@hooks/usePodcastPlayerStyle";
import ConfigurationSettings from "@particles/configurationSettings/ConfigurationSettings";
import { StickyAudioPlayer } from "@sphtech/web2-audio-player";
import { CDP_EXIT_INTERVENTION_MODULE_ID } from "@util/constant";
import { cn } from "@util/helpers";
import {
  newRelicAgentUrl,
  newRelicConfigScript,
} from "@web2/providers/NewRelic";

import Favicon from "./components/Favicon/Favicon";
import { RouteFactory, routesToExcludeFromLightbox } from "./routePaths";
import { Route } from "../../.react-router/types/src/app/+types/root";

// Order of imports will affect default margins on html elements
import "@styles/index.css";
import "@sphtech/web2-audio-player/styles.css";

// Wrapper component that sets up the HTML shell and global scripts
export const Layout = ({ children }: PropsWithChildren) => {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <meta name="google-play-app" content="app-id=com.ps.bt" />
        <meta name="apple-itunes-app" content="app-id=531275824" />
        <link rel="manifest" href="/web2-assets/manifest.json" />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@100..900&display=swap"
          rel="stylesheet"
        />

        <Favicon />

        <script src="//static.chartbeat.com/js/chartbeat_mab.js"></script>

        {/* Initialize the google variables. */}
        {/* Add a place to store the slot name variable. */}
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `
              var googletag = googletag || {};
              googletag.cmd = googletag.cmd || [];
              googletag.slots = googletag.slots || {};
            `,
          }}
        />

        {/* New relic script. */}
        {newRelicConfigScript && newRelicAgentUrl ? (
          <>
            <script
              dangerouslySetInnerHTML={{ __html: newRelicConfigScript }}
            />
            <script type="text/javascript" src={newRelicAgentUrl} async />
          </>
        ) : null}

        <script
          type="text/javascript"
          src="https://cdn.sphlabs.com/suid/suid.min.js"
        />

        <ConfigurationSettings />

        <Meta />

        <Links />
      </head>

      <body>
        <AdvertisementOverlay />
        <div id="postitial_holder"></div>

        {children}
        <Scripts />

        <div id="mySPHLightbox"></div>
      </body>
    </html>
  );
};

// Main application component
export default function App() {
  useAllCustomHooks();
  // ScrollRestoration emits warnings during SSR as it uses useLayoutEffect
  // Don't render that during SSR.
  const [render, setRender] = useState(false);

  const { pathname } = useLocation();

  useEffect(() => {
    setRender(true);
  }, []);

  const isCatfishAd = usePodcastPlayerStyle();

  const isWithAuth = !routesToExcludeFromLightbox.includes(pathname);

  return (
    <StrictMode>
      <CookiesProvider>
        <ProvidersRoot isWithAuth={isWithAuth} pathname={pathname}>
          <main
            className="flex w-screen max-w-full flex-col antialiased"
            id="background_container"
          >
            <div
              className="mx-auto flex w-full flex-grow flex-col items-center"
              id="content-container"
            >
              <Outlet />
            </div>
          </main>

          <StickyAudioPlayer
            mobilePlayerClassName={cn("z-[1000]", {
              "!bottom-[100px]": isCatfishAd,
            })}
          />
        </ProvidersRoot>
      </CookiesProvider>

      {/* ScrollRestoration emits warnings during client-side rendering */}
      {render ? <ScrollRestoration /> : null}

      {/* Disable Exit Intervention on MBO page */}
      {pathname !== RouteFactory.mbo ? (
        <CASBlock cdpId={CDP_EXIT_INTERVENTION_MODULE_ID} />
      ) : null}

      <PackageVersionTag />
      <PageGeneratedAtTag />
    </StrictMode>
  );
}

// Runs in the browser and catches errors that occur during rendering of React components on the client.
// I.e Catches that occur during CLIENT side routing
// Errors during rendering, lifecycle methods, and constructors in the React component tree.
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    return (
      <>
        <h1>is Route Error Response</h1>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}
